/**
 * maskEmail.ts — show a patient WHICH address a link went to without printing
 * the whole of it on the screen.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. First caller: the checkout's
 * "Confirm your email to continue" hold (2026-09-26).
 *
 * THE SHAPE: the first character of the local part, a FIXED three bullets, then
 * `@` and the domain as typed — `pat.smith@example.com` → `p•••@example.com`.
 *  - The bullets are a fixed count, so the mask does not leak the local part's
 *    length.
 *  - The DOMAIN STAYS. It is what tells a patient which inbox to open (work or
 *    personal), and it is their own address on their own signed-in screen; the
 *    local part is the bit that identifies them over a shoulder or in a
 *    screenshot sent to support.
 *  - Anything that is not one non-empty local part, one `@` and a non-empty
 *    domain answers null, and the caller says "your email address" instead —
 *    never a half-masked string built from something that is not an address.
 *
 * `spoken` is the same fact for a screen reader, which would otherwise read
 * three "bullet"s: "an address starting with p, at example.com".
 */
export interface MaskedEmail {
  /** What is drawn: `p•••@example.com`. */
  shown: string;
  /** What is read: `an address starting with p, at example.com`. */
  spoken: string;
}

const MASK = '•••';

export function maskEmail(email: string | null | undefined): MaskedEmail | null {
  const value = (email ?? '').trim();
  const at = value.lastIndexOf('@');
  if (at <= 0 || at !== value.indexOf('@')) return null;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (!domain || /\s/.test(value)) return null;
  const first = Array.from(local)[0];
  return {
    shown: `${first}${MASK}@${domain}`,
    spoken: `an address starting with ${first}, at ${domain}`,
  };
}
